import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Banner, Button, Field, Input } from '@acs/ui';
import { AccountEmail } from '@acs/shared';
import { useI18n } from '../../i18n/index.js';
import { requestPasswordReset } from '../../shared/api/auth.js';
import { friendlyError } from '../../shared/api/errors.js';
import { AuthShell } from './AuthShell.js';

/**
 * "Forgot password?" (doc 88). The confirmation reads the same whether or not
 * the address has an account — the server answers identically either way, and
 * this screen must not undo that by guessing.
 */
export function ForgotPasswordPage() {
  const { t, locale } = useI18n();
  const initial = (useLocation().state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(initial);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    const parsed = AccountEmail.safeParse(email);
    if (!parsed.success) {
      setError(t('authError.invalidEmail'));
      return;
    }
    setBusy(true);
    try {
      await requestPasswordReset(parsed.data, locale);
      setSentTo(parsed.data);
    } catch (e) {
      setError(friendlyError(t, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t('forgot.title')} lede={sentTo ? undefined : t('forgot.lede')}>
      {sentTo ? (
        <Banner tone="success">{t('forgot.sent', { email: sentTo })}</Banner>
      ) : (
        <form className="auth__fields" onSubmit={(ev) => void onSubmit(ev)} noValidate>
          {error && <Banner tone="danger">{error}</Banner>}
          <Field label={t('auth.email')}>
            <Input
              type="email"
              autoComplete="email"
              required
              maxLength={254}
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
          </Field>
          <Button type="submit" size="lg" block loading={busy}>
            {t('forgot.submit')}
          </Button>
        </form>
      )}
      <div className="auth__links auth__links--center">
        <Link className="auth__link" to="/signin">
          {t('forgot.back')}
        </Link>
      </div>
    </AuthShell>
  );
}
